import { Box, type ButtonProps, type LinkProps } from '@chakra-ui/react'
import { useRouter } from 'next/router'
import React, { type MouseEventHandler, type ReactElement, useCallback } from 'react'

import { useAppContext } from '@/client/context/app'
import { useUser } from '@/client/context/user'
import { hasUserRole, isUserDefined } from '@/client/services/user'

import AuthLink from './AuthLink'

// If the user is not logged in and approved, intercept clicks to child components and redirect
// them. If logged out, redirect them to sign up page. If unapproved, show them a modal and block the action.
export default function RequireLoginAndApproval({
  children,
}: {
  children: ReactElement<{
    onClick: ButtonProps['onClick']
    href: LinkProps['href']
  }>
}): JSX.Element {
  const user = useUser()
  const router = useRouter()

  // Disables click events on child components, but still allow clicks on our wrapper components
  const disabledOnClick = useCallback<MouseEventHandler<HTMLElement>>(e => {
    if (e.currentTarget !== e.target) {
      e.stopPropagation()
    }
  }, [])

  const renderContext = useAppContext()

  const childrenClone = React.isValidElement(children)
    ? React.cloneElement(children, {
        // Intercept clicks and links of child components

        // TODO: Rewrite RequireLoginAndApproval to only accept one child, also type out the
        // kind of child it accepts.
        onClick: disabledOnClick,
        href: undefined,
      })
    : children

  if (!isUserDefined(user)) {
    return (
      <AuthLink mode="signup" returnTo={router.asPath}>
        {childrenClone}
      </AuthLink>
    )
  }
  if (hasUserRole(user, 'user:unapproved')) {
    return (
      <Box
        onClick={() => {
          renderContext.showAppModal({
            type: 'pending-approval',
            trackName: 'pending-approval',
            actionName: 'perform this action',
          })
        }}
        style={{ cursor: 'pointer' }}
      >
        {childrenClone}
      </Box>
    )
  }

  return children
}
